<template>
  <div class="complete-button">
    <complete-button
      :is-complete="isComplete"
      :is-flyout-open="isFlyoutOpen"
      :complete-text="completeText"
      :aria-expanded="isFlyoutOpen.toString()"
      aria-controls="multi-complete-flyout"
      :disabled="disabled"
      @click-action="toggleFlyout"
    />

    <!-- multi-complete flyout -->
    <outside-click
      v-if="isFlyoutOpen"
      ignore-query-selector=".-complete.-open"
      @outside-click-action="closeFlyout"
    >
      <div id="multi-complete-flyout" ref="flyout" class="button-flyout">
        <p>
          You have more than one of the same workout scheduled today. Which one would you like to
          complete?
        </p>
        <scheduled-complete-button
          v-for="scheduledItem in scheduledItems"
          :key="scheduledItem.id"
          :scheduled-id="scheduledItem.id"
          :entity-id="entityId"
          :entity-type="entityType"
          :entity-category="entityCategory"
          :is-complete="!!scheduledItem.isComplete"
          :custom-complete-button-text="customCompleteText(scheduledItem)"
          :custom-incomplete-button-text="customCompleteText(scheduledItem)"
          :tracker-defaults="trackerDefaults"
          :disabled="disabled || completeActionsDisabled"
          :is-secondary="true"
          @complete-updated="$emit('complete-updated', $event)"
        />
      </div>
    </outside-click>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { mapState, mapActions } from 'pinia'
import { useCalendarStore } from '@/stores/calendar'
import CompleteButton from '@/components/buttons/completes/complete-button.vue'
import ScheduledCompleteButton from '@/components/buttons/completes/scheduled-complete-button.vue'
import OutsideClick from '@/components/tool-tips/outside-click.vue'

/**
 * Handles detail view completes when the same item is scheduled multiple times.
 * - same schedulable item
 *
 * A flyout will open up to show each schedulable item (all the same entity)
 * and allow the user to complete each one individual.
 */
export default defineComponent({
  name: 'MultiCompleteButton',
  components: { CompleteButton, ScheduledCompleteButton, OutsideClick },
  props: {
    // Used for state.completes
    entityId: {
      type: Number,
      required: true,
    },
    // programs not supported as they are always scheduled
    entityType: {
      default: 'workout',
      type: String,
      validator: (val) => ['workout', 'custom_workout', 'article', 'wellness_video'].includes(val),
    },

    // only article types currrently
    entityCategory: {
      default: '',
      validator: (val) => ['', 'recipe'].includes(val),
    },

    // workouts { minutes, difficulty }
    // other trackers in the future
    trackerDefaults: {
      type: Object,
      default: () => ({}),
    },

    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['complete-updated'],

  data() {
    return {
      isFlyoutOpen: false,
    }
  },

  computed: {
    ...mapState(useCalendarStore, ['scheduledItems', 'completeActionsDisabled']),

    scheduledCount() {
      return this.scheduledItems.length
    },

    completedCount() {
      return this.scheduledItems.filter((item) => item.isComplete).length
    },

    isComplete() {
      return this.scheduledCount === this.completedCount
    },

    // used for workout complete button on complete
    // passed down to modal component
    completeType() {
      // articles
      if (['article'].includes(this.entityType)) {
        // recipes
        if (['recipe'].includes(this.entityCategory)) {
          return 'Recipe'
        }

        return 'Article'
      }

      if (this.entityType === 'wellness_video') {
        return 'Video'
      }

      // no programs

      // all others
      return 'Workout'
    },

    completeText() {
      return `${this.completeType} Complete`
    },
  },

  methods: {
    ...mapActions(useCalendarStore, ['setIsComplete']),

    customCompleteText(scheduledItem) {
      // program items
      if (scheduledItem?.plan?.id) {
        const programScheduledItems = this.scheduledItems.filter((si) => !!si.plan)
        let displayIndex = ''

        if (programScheduledItems.length > 1) {
          displayIndex = ` ${
            programScheduledItems.findIndex((si) => si.id === scheduledItem.id) + 1
          }`
        }

        return `Program ${this.completeType}${displayIndex}`
      }

      // indepentent items
      const indScheduledItems = this.scheduledItems.filter((si) => !si.plan)
      let displayIndex = ''

      if (indScheduledItems.length > 1) {
        displayIndex = ` ${indScheduledItems.findIndex((si) => si.id === scheduledItem.id) + 1}`
      }

      return `Independent ${this.completeType}${displayIndex}`
    },

    toggleFlyout() {
      this.isFlyoutOpen = !this.isFlyoutOpen
    },

    closeFlyout() {
      this.isFlyoutOpen = false
    },
  },
})
</script>
